import React, { useState } from 'react';
import { ErrorBoundary } from './src/components/ErrorBoundary';
import { useSyncState } from './src/hooks/useSyncState';
import { seedDatabase } from './src/seedData';
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { supabase } from './src/supabaseClient';
import LoginScreen from './src/components/LoginScreen';
import DashboardScreen from './pages/Dashboard';
import BudgetScreen from './pages/Budget';
import PlanningScreen from './pages/Planning';
import QualityScreen from './pages/Quality';
import PaymentsScreen from './pages/Payments';
import EDTScreen from './pages/EDT';
import ProjectSelection from './pages/ProjectSelection';
import PreventionScreen from './pages/Prevention';
import { AIChatWidget } from './src/components/ui/AIChatWidget';
import ReportBugWidget from './src/components/ui/ReportBugWidget';
import { useMsal } from '@azure/msal-react';
import WaitingRoom from './src/pages/WaitingRoom';
import GlobalAdminPanel from './src/pages/GlobalAdminPanel';
import { NotificationBell } from './src/components/ui/NotificationBell';
import { Capacitor } from '@capacitor/core';
import { checkForUpdate, RemoteVersionInfo, VersionInfo } from './src/utils/versionCheck';
import { Browser } from '@capacitor/browser';

if (typeof window !== 'undefined') {
    (window as any).seedSupabase = seedDatabase;
}

// --- Shared Types ---
import {
    Task, Level, ResourceProject, ResourceItem, BudgetFolder, BudgetItem, ApuItem, PhysicalUnit,
    Responsible, PermissionSet, Profile, ProjectInfo, CompanyConfig, ProtocolItem, Reviewer,
    ReviewerGroup, ProtocolConfig, ChecklistStatus, ChecklistValue, ProtocolHistoryEntry,
    ProtocolState, WeeklyAllocation, WeeklyPlan, Constraint, LookaheadPlan, ProjectProgressSnapshot,
    CNCCause, CNCGroup, ConstraintDefinition, ConstraintGroup, GanttBaseline, SubcontractType,
    Subcontractor, UnitDetail, StatementItem, Contract, PaymentStatement, Activity, GlobalUser,
    AppNotification, Incidence, MasterProtocol
} from './src/types';

const HelmetIcon = ({ className = "size-6" }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 4C7.58 4 4 7.58 4 12v2h16v-2c0-4.42-3.58-8-8-8z" />
        <path d="M11 4.5h2v8h-2z" opacity="0.15" fill="black" />
        <path d="M2 15c0-.55.45-1 1-1h18c.55 0 1 .45 1 1s-.45 1-1 1H3c-.55 0-1-.45-1-1z" />
    </svg>
);

const Sidebar = ({ onBack, currentUser, onLogout, hasPermission, companyConfig, isNative }: { onBack: () => void, currentUser?: GlobalUser, onLogout: () => void, hasPermission: (module: string, action: 'view' | 'edit') => boolean, companyConfig: CompanyConfig, isNative?: boolean }) => {
    const location = useLocation();
    if (isNative) return null;
    const isActive = (path: string) => location.pathname === path;

    const navItems = [
        { path: "/", label: "Dashboard", icon: "dashboard", show: true },
        { path: "/edt", label: "EDT", icon: "account_tree", show: hasPermission('mod_edt', 'view') },
        { path: "/planning", label: "Planificación", icon: "calendar_month", show: hasPermission('mod_planificacion', 'view') },
        { path: "/quality", label: "Control de Calidad", icon: "fact_check", show: hasPermission('mod_calidad', 'view') },
        { path: "/payments", label: "Subcontratos", icon: "handshake", show: hasPermission('mod_subcontratos', 'view') },
        { path: "/prevention", label: "Prevención (SSOMA)", icon: "helmet_safety", show: hasPermission('mod_prevencion', 'view') },
        { path: "/reports", label: "Reportes", icon: "bar_chart", show: hasPermission('mod_reportes', 'view') },
    ].filter(i => i.show !== false);

    return (
        <aside className="hidden md:flex h-full w-64 flex-col border-r border-[#e7ecf3] bg-white dark:bg-[#1a2634] dark:border-[#2a3644] flex-shrink-0 z-50">
            <div className="flex h-full flex-col justify-between p-4">
                <div className="flex flex-col gap-6">
                    <div className="flex items-center gap-3 px-2">
                        <button
                            onClick={onBack}
                            className="flex items-center justify-center size-8 rounded-full text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-primary transition-all mr-1"
                            title="Volver a Selección de Proyecto"
                        >
                            <span className="material-symbols-outlined text-[20px]">arrow_back</span>
                        </button>
                        <div className="flex items-center justify-center rounded-xl size-10 flex-shrink-0 overflow-hidden bg-white border border-slate-100 dark:border-slate-800">
                            <img src={companyConfig?.logo || '/assets/logo_ld.png'} alt="Logo" className="w-full h-full object-contain p-1" />
                        </div>
                        <div className="flex flex-col flex-1 truncate">
                            <h1 className="text-base font-bold leading-normal text-slate-900 dark:text-white truncate">K-LyDapp</h1>
                            <p className="text-xs font-normal text-slate-500 dark:text-slate-400 truncate">Gestionamos tu Obra</p>
                        </div>
                    </div>
                    <nav className="flex flex-col gap-2">
                        {navItems.map((item) => (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${isActive(item.path)
                                    ? "bg-primary/10 text-primary"
                                    : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                                    }`}
                            >
                                {item.icon === 'helmet_safety' ? (
                                    <HelmetIcon className="size-[24px] flex-shrink-0" />
                                ) : (
                                    <span className="material-symbols-outlined text-[24px] flex-shrink-0">{item.icon}</span>
                                )}
                                <span className="text-sm font-medium">{item.label}</span>
                            </Link>
                        ))}
                    </nav>
                </div>
                <div className="flex flex-col gap-2 border-t border-slate-100 dark:border-slate-800 pt-4">

                    {currentUser && (
                        <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between px-2">
                            <div className="flex items-center gap-3 overflow-hidden">
                                <div className="h-9 w-9 flex-shrink-0 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xs uppercase border border-primary/20">
                                    {(() => {
                                        if (!currentUser) return 'U';
                                        if (currentUser.name && !currentUser.name.toLowerCase().includes('usuario invitado') && currentUser.name !== currentUser.email) {
                                            return (currentUser.name.split(' ')[0]?.[0] || '') + (currentUser.name.split(' ')[1]?.[0] || '');
                                        }
                                        return currentUser.email.substring(0, 2).toUpperCase();
                                    })()}
                                </div>
                                <div className="flex flex-col truncate">
                                    <span className="text-sm font-bold text-slate-800 dark:text-white truncate">{currentUser.name}</span>
                                    <span className="text-[10px] font-medium text-slate-500 truncate uppercase">{currentUser.globalRole}</span>
                                </div>
                            </div>
                            <button 
                                onClick={onLogout}
                                className="text-slate-400 hover:text-red-500 transition-colors p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
                                title="Cerrar Sesión"
                            >
                                <span className="material-symbols-outlined text-[20px]">logout</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </aside>
    );
};

// (Notification components moved to src/components/ui/NotificationBell.tsx)

const MobileHeader = ({ onBack, currentUser, onToggleMenu, notifications, setNotifications, companyConfig, isNative, onLogout }: { onBack: () => void, currentUser?: GlobalUser, onToggleMenu: () => void, notifications?: AppNotification[], setNotifications?: React.Dispatch<React.SetStateAction<AppNotification[]>>, companyConfig: CompanyConfig, isNative?: boolean, onLogout?: () => void }) => {
    return (
        <header 
            className={`${isNative ? 'flex' : 'md:hidden flex'} items-center justify-between bg-white dark:bg-[#1a2634] border-b border-slate-200 dark:border-slate-800 px-4 flex-shrink-0 z-40`}
            style={{
                height: "calc(3.5rem + env(safe-area-inset-top, 0px))",
                paddingTop: "env(safe-area-inset-top, 0px)"
            }}
        >
            <div className="flex items-center gap-3">
                <button onClick={onBack} className="p-1 -ml-1 text-slate-500 hover:text-primary transition-colors">
                    <span className="material-symbols-outlined">arrow_back</span>
                </button>
                <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center rounded-lg size-8 flex-shrink-0 overflow-hidden bg-white border border-slate-100 dark:border-slate-800">
                        <img src={'/assets/play_store_icon.png'} alt="Icono K" className="w-full h-full object-contain p-0.5 rounded" />
                    </div>
                    <span className="font-bold text-sm text-slate-800 dark:text-white">K-LyDapp</span>
                </div>
            </div>
            
            <div className="flex items-center gap-3">
                {notifications && setNotifications && (
                    <NotificationBell notifications={notifications} setNotifications={setNotifications} currentUser={currentUser} />
                )}
                {currentUser && (
                    <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-[10px] uppercase border border-primary/20">
                        {(() => {
                            if (currentUser.name && !currentUser.name.toLowerCase().includes('usuario invitado') && currentUser.name !== currentUser.email) {
                                return (currentUser.name.split(' ')[0]?.[0] || '') + (currentUser.name.split(' ')[1]?.[0] || '');
                            }
                            return currentUser.email.substring(0, 2).toUpperCase();
                        })()}
                    </div>
                )}
                {onLogout && (
                    <button 
                        onClick={onLogout}
                        className="text-slate-400 hover:text-red-500 transition-colors p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
                        title="Cerrar Sesión"
                    >
                        <span className="material-symbols-outlined text-[20px]">logout</span>
                    </button>
                )}
            </div>
        </header>
    );
};

const MobileTabBar = ({ hasPermission, onToggleMenu, isNative, currentUser }: { hasPermission: (module: string, action: 'view'|'edit') => boolean, onToggleMenu: () => void, isNative?: boolean, currentUser?: GlobalUser }) => {
    const location = useLocation();
    const isActive = (path: string) => location.pathname === path;

    const navItems = [
        { path: "/", label: "Panel", icon: "dashboard", show: true },
        { path: "/planning", label: "Plan", icon: "calendar_month", show: hasPermission('mod_planificacion', 'view') },
        { path: "/quality", label: "Calidad", icon: "fact_check", show: hasPermission('mod_calidad', 'view') },
        { path: "/payments", label: "SC", icon: "handshake", show: hasPermission('mod_subcontratos', 'view') },
    ].filter(i => i.show !== false);

    return (
        <div className={`${isNative ? 'flex' : 'md:hidden flex'} bg-white dark:bg-[#1a2634] border-t border-slate-200 dark:border-slate-800 flex-shrink-0 relative z-40 pb-safe mt-auto`}>
            {navItems.map((item) => (
                <Link
                    key={item.path}
                    to={item.path}
                    className={`flex-1 py-2 flex flex-col items-center justify-center transition-colors ${isActive(item.path)
                        ? "text-primary"
                        : "text-slate-500 dark:text-slate-400"
                        }`}
                >
                    <span className="material-symbols-outlined text-[22px] mb-0.5">{item.icon}</span>
                    <span className="text-[10px] font-medium">{item.label}</span>
                </Link>
            ))}
            {currentUser && currentUser.globalRole === 'Administrador' && (
                <button
                    onClick={onToggleMenu}
                    className="flex-1 py-2 flex flex-col items-center justify-center text-slate-500 dark:text-slate-400 hover:text-primary transition-colors"
                >
                    <span className="material-symbols-outlined text-[22px] mb-0.5">menu</span>
                    <span className="text-[10px] font-medium">Menú</span>
                </button>
            )}
        </div>
    );
};

const MobileMenuDrawer = ({ isOpen, onClose, onLogout, hasPermission, isNative }: { isOpen: boolean, onClose: () => void, onLogout: () => void, hasPermission: (module: string, action: 'view'|'edit') => boolean, isNative?: boolean }) => {
    const location = useLocation();
    if (!isOpen) return null;

    const navItems = [
        { path: "/", label: "Dashboard", icon: "dashboard", show: true },
        { path: "/edt", label: "EDT (Estructura)", icon: "account_tree", show: hasPermission('mod_edt', 'view') },
        { path: "/planning", label: "Planificación", icon: "calendar_month", show: hasPermission('mod_planificacion', 'view') },
        { path: "/quality", label: "Control de Calidad", icon: "fact_check", show: hasPermission('mod_calidad', 'view') },
        { path: "/payments", label: "Subcontratos/SC", icon: "handshake", show: hasPermission('mod_subcontratos', 'view') },
        { path: "/prevention", label: "Prevención (SSOMA)", icon: "helmet_safety", show: hasPermission('mod_prevencion', 'view') },
        { path: "/reports", label: "Reportes", icon: "bar_chart", show: hasPermission('mod_reportes', 'view') },
    ].filter(i => i.show !== false);

    return (
        <div className={`${isNative ? 'fixed' : 'md:hidden fixed'} inset-0 z-[100] flex flex-col bg-white dark:bg-[#1a2634]`}>
            <div 
                className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-800"
                style={{
                    paddingTop: "calc(1rem + env(safe-area-inset-top, 0px))"
                }}
            >
                <span className="font-bold text-lg text-slate-800 dark:text-white">Menú Principal</span>
                <button onClick={onClose} className="p-2 text-slate-500 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800">
                    <span className="material-symbols-outlined">close</span>
                </button>
            </div>
            <div className="flex-1 overflow-y-auto py-4 px-4 flex flex-col gap-2">
                {navItems.map((item) => (
                    <Link
                        key={item.path}
                        to={item.path}
                        onClick={onClose}
                        className={`flex items-center gap-4 px-4 py-4 rounded-xl transition-colors ${location.pathname === item.path
                            ? "bg-primary text-white"
                            : "bg-slate-50 dark:bg-slate-800/50 text-slate-700 dark:text-slate-300"
                            }`}
                    >
                        {item.icon === 'helmet_safety' ? (
                            <HelmetIcon className="size-[24px] flex-shrink-0" />
                        ) : (
                            <span className="material-symbols-outlined text-[24px] flex-shrink-0">{item.icon}</span>
                        )}
                        <span className="font-medium">{item.label}</span>
                    </Link>
                ))}
            </div>
            <div className="p-6 border-t border-slate-200 dark:border-slate-800 pb-safe">
                <button 
                    onClick={onLogout}
                    className="w-full flex items-center justify-center gap-2 py-4 bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400 font-bold rounded-xl"
                >
                    <span className="material-symbols-outlined">logout</span>
                    Cerrar Sesión
                </button>
            </div>
        </div>
    );
};

const AuthCallbackRedirect = () => {
    const navigate = useNavigate();
    React.useEffect(() => {
        navigate('/', { replace: true });
    }, [navigate]);
    return null;
};

import ReportsScreen from './pages/Reports';

const App: React.FC = () => {
    const [isNative] = useState<boolean>(() => {
        try {
            localStorage.removeItem('isNative');
        } catch (e) {}
        return Capacitor.isNativePlatform();
    });

    // --- Version Update Check State ---
    const [updateInfo, setUpdateInfo] = useState<{
        installed: VersionInfo;
        latest: RemoteVersionInfo;
    } | null>(null);
    const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);

    React.useEffect(() => {
        const runVersionCheck = async () => {
            const isDismissed = sessionStorage.getItem('klydapp_update_dismissed') === 'true';
            if (isDismissed) return;

            const update = await checkForUpdate();
            if (update && update.hasUpdate) {
                setUpdateInfo({
                    installed: update.installed,
                    latest: update.latest
                });
                setShowUpdateModal(true);
            }
        };

        const timer = setTimeout(() => {
            runVersionCheck();
        }, 1500);

        return () => clearTimeout(timer);
    }, []);

    const handleDismissUpdate = () => {
        sessionStorage.setItem('klydapp_update_dismissed', 'true');
        setShowUpdateModal(false);
    };

    const handlePerformUpdate = async () => {
        if (!updateInfo) return;
        const url = updateInfo.latest.update_url;
        if (Capacitor.isNativePlatform()) {
            try {
                await Browser.open({ url });
            } catch (e) {
                console.error("Error opening Capacitor Browser:", e);
                window.open(url, '_blank');
            }
        } else {
            window.open(url, '_blank');
        }
    };

    // --- Global State ---

    // Incidences State
    const [incidences, setIncidences] = useSyncState<Incidence[]>('data', 'incidences', []);

    // Projects Multi-support
    const [projects, setProjects] = useSyncState<(ProjectInfo & { id: string })[]>('data', 'projects', [
        { id: '1', name: 'Achs Las Condes', startDate: '2026-01-05', endDate: '2026-12-31', status: 'Active' },
        { id: '2', name: 'Edificio Kennedy', startDate: '2026-02-15', endDate: '2026-11-20', status: 'Active' }
    ]);

    // Company Branding Configuration
    const [companyConfig, setCompanyConfig] = useSyncState<CompanyConfig>('data', 'companyConfig', {
        name: 'Constructora LD',
        rut: '',
        razonSocial: '',
        direccion: '',
        logo: undefined
    });



    const { accounts, instance } = useMsal();
    const currentMsalAccount = accounts[0];

    const [supabaseSession, setSupabaseSession] = useState<any>(null);
    const [checkingSession, setCheckingSession] = useState(true);

    React.useEffect(() => {
        supabase.auth.getSession().then(({ data: { session } }) => {
            setSupabaseSession(session);
            setCheckingSession(false);
        });

        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange((_event, session) => {
            setSupabaseSession(session);
        });

        return () => subscription.unsubscribe();
    }, []);

    const [globalUsers, setGlobalUsers] = useSyncState<GlobalUser[]>('data', 'globalUsers', []);
    const [globalProfiles, setGlobalProfiles] = useSyncState<Profile[]>('data', 'globalProfiles', []);
    
    // Global Masters
    const [globalCNCGroups, setGlobalCNCGroups] = useSyncState<CNCGroup[]>('data', 'globalCNCGroups', [
        { id: 'g1', name: 'Materiales', causes: [] }
    ]);
    const [globalConstraintGroups, setGlobalConstraintGroups] = useSyncState<ConstraintGroup[]>('data', 'globalConstraintGroups', [
        { id: 'cg1', name: 'Ingeniería', definitions: [{ id: 'cd1', name: 'Falta planos detalle' }, { id: 'cd2', name: 'RDI Pendiente' }] },
        { id: 'cg2', name: 'Abastecimiento', definitions: [{ id: 'cd3', name: 'Falta Materiales' }, { id: 'cd4', name: 'Falta Equipos' }] }
    ]);
    const [globalSubcontractTypes, setGlobalSubcontractTypes] = useSyncState<SubcontractType[]>('data', 'globalSubcontractTypes', [
        { id: 'st1', name: 'Suma Alzada' },
        { id: 'st2', name: 'Serie de Precios Unitarios' },
        { id: 'st3', name: 'Administración Delegada' }
    ]);
    const [globalProtocolTemplates, setGlobalProtocolTemplates] = useSyncState<MasterProtocol[]>('data', 'globalProtocolTemplates', [
        {
            id: 'mp-default-1',
            protocolName: 'Protocolo de Hormigonado',
            code: 'PRT-H',
            category: 'Obra Gruesa',
            subcategory: 'Fundaciones',
            items: [
                { id: 'pi1', text: 'Limpieza de superficie y moldajes', additionalInfo: 'Verificar ausencia de escombros' },
                { id: 'pi2', text: 'Trazado y nivelación de cotas', additionalInfo: 'Tolerancia ± 5mm' }
            ]
        }
    ]);
    const [globalSSOMAChecklists, setGlobalSSOMAChecklists] = useSyncState<MasterProtocol[]>('data', 'globalSSOMAChecklists', [
        {
            id: 'sc-default-1',
            protocolName: 'Charla de 5 Minutos',
            code: 'SSOMA-C5M',
            category: 'Charla',
            subcategory: 'Charla General',
            items: [
                { id: 'sci1', text: 'Presentación del tema del día', additionalInfo: 'Explicar riesgos asociados' },
                { id: 'sci2', text: 'Firmas de asistencia', additionalInfo: 'Registrar a todos los participantes' }
            ]
        }
    ]);
    const [globalSSOMAEvents, setGlobalSSOMAEvents] = useSyncState<CNCGroup[]>('data', 'globalSSOMAEvents', [
        {
            id: 'se1',
            name: 'Condición Subestándar',
            causes: [
                { id: 'sec1', name: 'Falta de orden y limpieza' },
                { id: 'sec2', name: 'Iluminación deficiente' }
            ]
        },
        {
            id: 'se2',
            name: 'Acto Subestándar',
            causes: [
                { id: 'sec3', name: 'No usar EPP obligatorio' },
                { id: 'sec4', name: 'Uso inadecuado de herramientas' }
            ]
        }
    ]);
    const [showAdminPanel, setShowAdminPanel] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // Migrate missing default profiles automatically
    React.useEffect(() => {
        const defaultProfiles = [
            {
                id: 'prof-admin',
                name: 'Administrador de Obra',
                permissions: {
                    'mod_edt': { view: true, edit: false }, 'edt_unidades': { view: true, edit: true }, 'edt_niveles': { view: true, edit: true }, 'edt_editar': { view: true, edit: true }, 'conf_avance_grafico': { view: true, edit: true },
                    'mod_planificacion': { view: true, edit: false },
                    'mod_plan_maestra': { view: true, edit: false }, 'pm_gantt_edit': { view: true, edit: true }, 'pm_gantt_imp': { view: true, edit: true }, 'pm_gantt_exp': { view: true, edit: true }, 'pm_reprog': { view: true, edit: true }, 'pm_historial': { view: true, edit: true }, 'pm_sync': { view: true, edit: true },
                    'mod_plan_intermedia': { view: true, edit: false }, 'pi_historial': { view: true, edit: false }, 'pi_ite': { view: true, edit: false }, 'pi_generar': { view: true, edit: true }, 'pi_planificaciones': { view: true, edit: true }, 'pi_restricciones': { view: true, edit: true }, 'pi_eliminar': { view: true, edit: true },
                    'mod_plan_semanal': { view: true, edit: false }, 'ps_historial': { view: true, edit: false }, 'ps_crear': { view: true, edit: true }, 'ps_desglose': { view: true, edit: true }, 'ps_planificaciones': { view: true, edit: true }, 'ps_gantt_avance': { view: true, edit: true }, 'ps_eliminar': { view: true, edit: true },
                    'mod_calidad': { view: true, edit: false }, 'cc_config': { view: true, edit: true }, 'cc_completar': { view: true, edit: true },
                    'mod_subcontratos': { view: true, edit: false }, 'sc_config': { view: true, edit: true }, 'sc_crear': { view: true, edit: true }, 'sc_lista': { view: true, edit: true }, 'sc_anadir': { view: true, edit: true }, 'sc_editar': { view: true, edit: true }, 'sc_inactivar': { view: true, edit: true }, 'sc_anular': { view: true, edit: true }, 'sc_activar': { view: true, edit: true }, 'sc_eliminar': { view: true, edit: true }, 'sc_generar_ep': { view: true, edit: true }, 'sc_anexo': { view: true, edit: true }, 'sc_ver_ep': { view: true, edit: true }, 'sc_ep_autorizar': { view: true, edit: true }, 'sc_ep_eliminar': { view: true, edit: true },
                    'mod_movil': { view: true, edit: false },
                    'mod_panel': { view: true, edit: false }, 'panel_semanal': { view: true, edit: false }, 'panel_intermedia': { view: true, edit: false }, 'panel_ep': { view: true, edit: false }, 'panel_protocolos': { view: true, edit: false },
                    'mod_movil_intermedia': { view: true, edit: false }, 'movil_pi_restricciones': { view: false, edit: true },
                    'mod_movil_semanal': { view: true, edit: false }, 'movil_ps_tareas': { view: false, edit: true },
                    'mod_movil_calidad': { view: true, edit: false }, 'movil_cc_todos': { view: true, edit: false }, 'movil_cc_asignados': { view: true, edit: false }, 'movil_cc_completar': { view: false, edit: true },
                    'mod_movil_sc': { view: true, edit: false }, 'movil_sc_ver_ep': { view: true, edit: false }, 'movil_sc_generar_ep': { view: false, edit: true },
                    'mod_prevencion': { view: true, edit: true },
                    'mod_reportes': { view: false, edit: false },
                    'rep_reportes': { view: false, edit: false },
                    'rep_disenar': { view: false, edit: false }
                }
            },
            {
                id: 'prof-jefe',
                name: 'Jefe de Obra (Terreno)',
                permissions: {
                    'mod_edt': { view: true, edit: false }, 'edt_unidades': { view: true, edit: false }, 'edt_niveles': { view: true, edit: false }, 'edt_editar': { view: false, edit: false }, 'conf_avance_grafico': { view: true, edit: false },
                    'mod_planificacion': { view: true, edit: false },
                    'mod_plan_maestra': { view: true, edit: false }, 'pm_gantt_edit': { view: false, edit: false }, 'pm_gantt_imp': { view: false, edit: false }, 'pm_gantt_exp': { view: false, edit: false }, 'pm_reprog': { view: false, edit: false }, 'pm_historial': { view: true, edit: false }, 'pm_sync': { view: false, edit: false },
                    'mod_plan_intermedia': { view: true, edit: false }, 'pi_historial': { view: true, edit: false }, 'pi_ite': { view: true, edit: false }, 'pi_generar': { view: true, edit: true }, 'pi_planificaciones': { view: true, edit: false }, 'pi_restricciones': { view: true, edit: true }, 'pi_eliminar': { view: true, edit: false },
                    'mod_plan_semanal': { view: true, edit: false }, 'ps_historial': { view: true, edit: false }, 'ps_crear': { view: true, edit: true }, 'ps_desglose': { view: true, edit: true }, 'ps_planificaciones': { view: true, edit: false }, 'ps_gantt_avance': { view: false, edit: false }, 'ps_eliminar': { view: true, edit: false },
                    'mod_calidad': { view: true, edit: false }, 'cc_config': { view: false, edit: false }, 'cc_completar': { view: true, edit: true },
                    'mod_subcontratos': { view: false, edit: false }, 'sc_crear': { view: false, edit: false }, 'sc_lista': { view: false, edit: false }, 'sc_anadir': { view: false, edit: false }, 'sc_editar': { view: false, edit: false }, 'sc_inactivar': { view: false, edit: false }, 'sc_anular': { view: false, edit: false }, 'sc_activar': { view: false, edit: false }, 'sc_eliminar': { view: false, edit: false }, 'sc_generar_ep': { view: false, edit: false }, 'sc_anexo': { view: false, edit: false }, 'sc_ver_ep': { view: false, edit: false },
                    'mod_movil': { view: true, edit: false },
                    'mod_panel': { view: true, edit: false }, 'panel_semanal': { view: true, edit: false }, 'panel_intermedia': { view: true, edit: false }, 'panel_ep': { view: false, edit: false }, 'panel_protocolos': { view: true, edit: false },
                    'mod_movil_intermedia': { view: true, edit: false }, 'movil_pi_restricciones': { view: false, edit: true },
                    'mod_movil_semanal': { view: true, edit: false }, 'movil_ps_tareas': { view: false, edit: true },
                    'mod_movil_calidad': { view: true, edit: false }, 'movil_cc_todos': { view: true, edit: false }, 'movil_cc_asignados': { view: true, edit: false }, 'movil_cc_completar': { view: false, edit: true },
                    'mod_movil_sc': { view: false, edit: false }, 'movil_sc_ver_ep': { view: false, edit: false }, 'movil_sc_generar_ep': { view: false, edit: false },
                    'mod_prevencion': { view: true, edit: true },
                    'mod_reportes': { view: false, edit: false },
                    'rep_reportes': { view: false, edit: false },
                    'rep_disenar': { view: false, edit: false }
                }
            },
            {
                id: 'prof-calidad',
                name: 'Encargado de Calidad',
                permissions: {
                    'mod_edt': { view: true, edit: false }, 'edt_unidades': { view: true, edit: false }, 'edt_niveles': { view: true, edit: false }, 'edt_editar': { view: false, edit: false }, 'conf_avance_grafico': { view: true, edit: false },
                    'mod_planificacion': { view: true, edit: false },
                    'mod_plan_maestra': { view: true, edit: false }, 'pm_gantt_edit': { view: false, edit: false }, 'pm_gantt_imp': { view: false, edit: false }, 'pm_gantt_exp': { view: false, edit: false }, 'pm_reprog': { view: false, edit: false }, 'pm_historial': { view: true, edit: false }, 'pm_sync': { view: false, edit: false },
                    'mod_plan_intermedia': { view: true, edit: false }, 'pi_historial': { view: true, edit: false }, 'pi_ite': { view: true, edit: false }, 'pi_generar': { view: false, edit: false }, 'pi_planificaciones': { view: true, edit: false }, 'pi_restricciones': { view: false, edit: false }, 'pi_eliminar': { view: false, edit: false },
                    'mod_plan_semanal': { view: true, edit: false }, 'ps_historial': { view: true, edit: false }, 'ps_crear': { view: false, edit: false }, 'ps_desglose': { view: false, edit: false }, 'ps_planificaciones': { view: true, edit: false }, 'ps_gantt_avance': { view: false, edit: false }, 'ps_eliminar': { view: false, edit: false },
                    'mod_calidad': { view: true, edit: false }, 'cc_config': { view: true, edit: true }, 'cc_completar': { view: true, edit: true },
                    'mod_subcontratos': { view: false, edit: false }, 'sc_crear': { view: false, edit: false }, 'sc_lista': { view: false, edit: false }, 'sc_anadir': { view: false, edit: false }, 'sc_editar': { view: false, edit: false }, 'sc_inactivar': { view: false, edit: false }, 'sc_anular': { view: false, edit: false }, 'sc_activar': { view: false, edit: false }, 'sc_eliminar': { view: false, edit: false }, 'sc_generar_ep': { view: false, edit: false }, 'sc_anexo': { view: false, edit: false }, 'sc_ver_ep': { view: false, edit: false },
                    'mod_movil': { view: true, edit: false },
                    'mod_panel': { view: true, edit: false }, 'panel_semanal': { view: true, edit: false }, 'panel_intermedia': { view: true, edit: false }, 'panel_ep': { view: false, edit: false }, 'panel_protocolos': { view: true, edit: false },
                    'mod_movil_intermedia': { view: true, edit: false }, 'movil_pi_restricciones': { view: false, edit: false },
                    'mod_movil_semanal': { view: true, edit: false }, 'movil_ps_tareas': { view: false, edit: false },
                    'mod_movil_calidad': { view: true, edit: false }, 'movil_cc_todos': { view: true, edit: false }, 'movil_cc_asignados': { view: true, edit: false }, 'movil_cc_completar': { view: false, edit: true },
                    'mod_movil_sc': { view: false, edit: false }, 'movil_sc_ver_ep': { view: false, edit: false }, 'movil_sc_generar_ep': { view: false, edit: false },
                    'mod_prevencion': { view: true, edit: false },
                    'mod_reportes': { view: false, edit: false },
                    'rep_reportes': { view: false, edit: false },
                    'rep_disenar': { view: false, edit: false }
                }
            },
            {
                id: 'prof-oficina',
                name: 'Encargado de Oficina Técnica',
                permissions: {
                    'mod_edt': { view: true, edit: false }, 'edt_unidades': { view: true, edit: true }, 'edt_niveles': { view: true, edit: true }, 'edt_editar': { view: true, edit: true }, 'conf_avance_grafico': { view: true, edit: true },
                    'mod_planificacion': { view: true, edit: false },
                    'mod_plan_maestra': { view: true, edit: false }, 'pm_gantt_edit': { view: true, edit: true }, 'pm_gantt_imp': { view: true, edit: true }, 'pm_gantt_exp': { view: true, edit: true }, 'pm_reprog': { view: true, edit: true }, 'pm_historial': { view: true, edit: true }, 'pm_sync': { view: true, edit: true },
                    'mod_plan_intermedia': { view: true, edit: false }, 'pi_historial': { view: true, edit: false }, 'pi_ite': { view: true, edit: false }, 'pi_generar': { view: true, edit: true }, 'pi_planificaciones': { view: true, edit: true }, 'pi_restricciones': { view: true, edit: true }, 'pi_eliminar': { view: true, edit: true },
                    'mod_plan_semanal': { view: true, edit: false }, 'ps_historial': { view: true, edit: false }, 'ps_crear': { view: true, edit: true }, 'ps_desglose': { view: true, edit: true }, 'ps_planificaciones': { view: true, edit: true }, 'ps_gantt_avance': { view: true, edit: true }, 'ps_eliminar': { view: true, edit: true },
                    'mod_calidad': { view: true, edit: false }, 'cc_config': { view: true, edit: false }, 'cc_completar': { view: true, edit: false },
                    'mod_subcontratos': { view: true, edit: false }, 'sc_config': { view: true, edit: true }, 'sc_crear': { view: true, edit: true }, 'sc_lista': { view: true, edit: true }, 'sc_anadir': { view: true, edit: true }, 'sc_editar': { view: true, edit: true }, 'sc_inactivar': { view: true, edit: true }, 'sc_anular': { view: true, edit: true }, 'sc_activar': { view: true, edit: true }, 'sc_eliminar': { view: true, edit: true }, 'sc_generar_ep': { view: true, edit: true }, 'sc_anexo': { view: true, edit: true }, 'sc_ver_ep': { view: true, edit: true }, 'sc_ep_autorizar': { view: true, edit: true }, 'sc_ep_eliminar': { view: true, edit: true },
                    'mod_movil': { view: true, edit: false },
                    'mod_panel': { view: true, edit: false }, 'panel_semanal': { view: true, edit: false }, 'panel_intermedia': { view: true, edit: false }, 'panel_ep': { view: true, edit: false }, 'panel_protocolos': { view: true, edit: false },
                    'mod_movil_intermedia': { view: true, edit: false }, 'movil_pi_restricciones': { view: false, edit: true },
                    'mod_movil_semanal': { view: true, edit: false }, 'movil_ps_tareas': { view: false, edit: true },
                    'mod_movil_calidad': { view: true, edit: false }, 'movil_cc_todos': { view: true, edit: false }, 'movil_cc_asignados': { view: true, edit: false }, 'movil_cc_completar': { view: false, edit: false },
                    'mod_movil_sc': { view: true, edit: false }, 'movil_sc_ver_ep': { view: true, edit: false }, 'movil_sc_generar_ep': { view: false, edit: true },
                    'mod_prevencion': { view: true, edit: false },
                    'mod_reportes': { view: false, edit: false },
                    'rep_reportes': { view: false, edit: false },
                    'rep_disenar': { view: false, edit: false }
                }
            },
            {
                id: 'prof-asistente',
                name: 'Asistente de Oficina Técnica',
                permissions: {
                    'mod_incidencias': { view: true, edit: false },
                    'mod_edt': { view: true, edit: false }, 'edt_unidades': { view: true, edit: false }, 'edt_niveles': { view: true, edit: false }, 'edt_editar': { view: false, edit: false }, 'conf_avance_grafico': { view: true, edit: false },
                    'mod_planificacion': { view: true, edit: false },
                    'mod_plan_maestra': { view: true, edit: false }, 'pm_gantt_edit': { view: false, edit: false }, 'pm_gantt_imp': { view: false, edit: false }, 'pm_gantt_exp': { view: false, edit: false }, 'pm_reprog': { view: false, edit: false }, 'pm_historial': { view: true, edit: false }, 'pm_sync': { view: false, edit: false },
                    'mod_plan_intermedia': { view: true, edit: false }, 'pi_historial': { view: true, edit: false }, 'pi_ite': { view: true, edit: false }, 'pi_generar': { view: false, edit: false }, 'pi_planificaciones': { view: true, edit: false }, 'pi_restricciones': { view: true, edit: true }, 'pi_eliminar': { view: false, edit: false },
                    'mod_plan_semanal': { view: true, edit: false }, 'ps_historial': { view: true, edit: false }, 'ps_crear': { view: false, edit: false }, 'ps_desglose': { view: false, edit: false }, 'ps_planificaciones': { view: true, edit: false }, 'ps_gantt_avance': { view: false, edit: false }, 'ps_eliminar': { view: false, edit: false },
                    'mod_calidad': { view: true, edit: false }, 'cc_config': { view: true, edit: false }, 'cc_completar': { view: true, edit: true },
                    'mod_subcontratos': { view: true, edit: false }, 'sc_crear': { view: false, edit: false }, 'sc_lista': { view: true, edit: false }, 'sc_anadir': { view: false, edit: false }, 'sc_editar': { view: false, edit: false }, 'sc_inactivar': { view: false, edit: false }, 'sc_anular': { view: false, edit: false }, 'sc_activar': { view: false, edit: false }, 'sc_eliminar': { view: false, edit: false }, 'sc_generar_ep': { view: false, edit: false }, 'sc_anexo': { view: false, edit: false }, 'sc_ver_ep': { view: true, edit: false },
                    'mod_movil': { view: true, edit: false },
                    'mod_panel': { view: true, edit: false }, 'panel_semanal': { view: true, edit: false }, 'panel_intermedia': { view: true, edit: false }, 'panel_ep': { view: false, edit: false }, 'panel_protocolos': { view: true, edit: false },
                    'mod_movil_intermedia': { view: true, edit: false }, 'movil_pi_restricciones': { view: false, edit: true },
                    'mod_movil_semanal': { view: true, edit: false }, 'movil_ps_tareas': { view: false, edit: true },
                    'mod_movil_calidad': { view: true, edit: false }, 'movil_cc_todos': { view: true, edit: false }, 'movil_cc_asignados': { view: true, edit: false }, 'movil_cc_completar': { view: false, edit: false },
                    'mod_movil_sc': { view: true, edit: false }, 'movil_sc_ver_ep': { view: true, edit: false }, 'movil_sc_generar_ep': { view: false, edit: true },
                    'mod_prevencion': { view: true, edit: false },
                    'mod_reportes': { view: false, edit: false },
                    'rep_reportes': { view: false, edit: false },
                    'rep_disenar': { view: false, edit: false }
                }
            },
            {
                id: 'prof-prevencion',
                name: 'Prevencionista de Riesgos',
                permissions: {
                    'mod_edt': { view: true, edit: false }, 'edt_unidades': { view: true, edit: false }, 'edt_niveles': { view: true, edit: false }, 'edt_editar': { view: false, edit: false }, 'conf_avance_grafico': { view: true, edit: false },
                    'mod_planificacion': { view: true, edit: false },
                    'mod_plan_maestra': { view: true, edit: false }, 'pm_gantt_edit': { view: false, edit: false }, 'pm_gantt_imp': { view: false, edit: false }, 'pm_gantt_exp': { view: false, edit: false }, 'pm_reprog': { view: false, edit: false }, 'pm_historial': { view: true, edit: false }, 'pm_sync': { view: false, edit: false },
                    'mod_plan_intermedia': { view: true, edit: false }, 'pi_historial': { view: true, edit: false }, 'pi_ite': { view: true, edit: false }, 'pi_generar': { view: false, edit: false }, 'pi_planificaciones': { view: true, edit: false }, 'pi_restricciones': { view: false, edit: false }, 'pi_eliminar': { view: false, edit: false },
                    'mod_plan_semanal': { view: true, edit: false }, 'ps_historial': { view: true, edit: false }, 'ps_crear': { view: false, edit: false }, 'ps_desglose': { view: false, edit: false }, 'ps_planificaciones': { view: true, edit: false }, 'ps_gantt_avance': { view: false, edit: false }, 'ps_eliminar': { view: false, edit: false },
                    'mod_calidad': { view: true, edit: false }, 'cc_config': { view: false, edit: false }, 'cc_completar': { view: false, edit: false },
                    'mod_subcontratos': { view: false, edit: false }, 'sc_crear': { view: false, edit: false }, 'sc_lista': { view: false, edit: false }, 'sc_anadir': { view: false, edit: false }, 'sc_editar': { view: false, edit: false }, 'sc_inactivar': { view: false, edit: false }, 'sc_anular': { view: false, edit: false }, 'sc_activar': { view: false, edit: false }, 'sc_eliminar': { view: false, edit: false }, 'sc_generar_ep': { view: false, edit: false }, 'sc_anexo': { view: false, edit: false }, 'sc_ver_ep': { view: false, edit: false },
                    'mod_movil': { view: true, edit: false },
                    'mod_panel': { view: true, edit: false }, 'panel_semanal': { view: true, edit: false }, 'panel_intermedia': { view: true, edit: false }, 'panel_ep': { view: false, edit: false }, 'panel_protocolos': { view: true, edit: false },
                    'mod_movil_intermedia': { view: true, edit: false }, 'movil_pi_restricciones': { view: false, edit: false },
                    'mod_movil_semanal': { view: true, edit: false }, 'movil_ps_tareas': { view: false, edit: false },
                    'mod_movil_calidad': { view: true, edit: false }, 'movil_cc_todos': { view: true, edit: false }, 'movil_cc_asignados': { view: true, edit: false }, 'movil_cc_completar': { view: false, edit: false },
                    'mod_movil_sc': { view: false, edit: false }, 'movil_sc_ver_ep': { view: false, edit: false }, 'movil_sc_generar_ep': { view: false, edit: false },
                    'mod_prevencion': { view: true, edit: true },
                    'mod_reportes': { view: false, edit: false },
                    'rep_reportes': { view: false, edit: false },
                    'rep_disenar': { view: false, edit: false }
                }
            },
            {
                id: 'prof-innovacion',
                name: 'Equipo Innovación',
                permissions: {
                    'mod_incidencias': { view: true, edit: true },
                    'mod_edt': { view: true, edit: true }, 'edt_unidades': { view: true, edit: true }, 'edt_niveles': { view: true, edit: true }, 'edt_editar': { view: true, edit: true }, 'conf_avance_grafico': { view: true, edit: true },
                    'mod_planificacion': { view: true, edit: true },
                    'mod_plan_maestra': { view: true, edit: true }, 'pm_gantt_edit': { view: true, edit: true }, 'pm_gantt_imp': { view: true, edit: true }, 'pm_gantt_exp': { view: true, edit: true }, 'pm_reprog': { view: true, edit: true }, 'pm_historial': { view: true, edit: true }, 'pm_sync': { view: true, edit: true },
                    'mod_plan_intermedia': { view: true, edit: true }, 'pi_historial': { view: true, edit: true }, 'pi_ite': { view: true, edit: true }, 'pi_generar': { view: true, edit: true }, 'pi_planificaciones': { view: true, edit: true }, 'pi_restricciones': { view: true, edit: true }, 'pi_eliminar': { view: true, edit: true },
                    'mod_plan_semanal': { view: true, edit: true }, 'ps_historial': { view: true, edit: true }, 'ps_crear': { view: true, edit: true }, 'ps_desglose': { view: true, edit: true }, 'ps_planificaciones': { view: true, edit: true }, 'ps_gantt_avance': { view: true, edit: true }, 'ps_eliminar': { view: true, edit: true },
                    'mod_calidad': { view: true, edit: true }, 'cc_config': { view: true, edit: true }, 'cc_completar': { view: true, edit: true },
                    'mod_subcontratos': { view: true, edit: true }, 'sc_config': { view: true, edit: true }, 'sc_crear': { view: true, edit: true }, 'sc_lista': { view: true, edit: true }, 'sc_anadir': { view: true, edit: true }, 'sc_editar': { view: true, edit: true }, 'sc_inactivar': { view: true, edit: true }, 'sc_anular': { view: true, edit: true }, 'sc_activar': { view: true, edit: true }, 'sc_eliminar': { view: true, edit: true }, 'sc_generar_ep': { view: true, edit: true }, 'sc_anexo': { view: true, edit: true }, 'sc_ver_ep': { view: true, edit: true }, 'sc_ep_autorizar': { view: true, edit: true }, 'sc_ep_eliminar': { view: true, edit: true },
                    'mod_movil': { view: true, edit: true },
                    'mod_panel': { view: true, edit: true }, 'panel_semanal': { view: true, edit: true }, 'panel_intermedia': { view: true, edit: true }, 'panel_ep': { view: true, edit: true }, 'panel_protocolos': { view: true, edit: true },
                    'mod_movil_intermedia': { view: true, edit: true }, 'movil_pi_restricciones': { view: true, edit: true },
                    'mod_movil_semanal': { view: true, edit: true }, 'movil_ps_tareas': { view: true, edit: true },
                    'mod_movil_calidad': { view: true, edit: true }, 'movil_cc_todos': { view: true, edit: true }, 'movil_cc_asignados': { view: true, edit: true }, 'movil_cc_completar': { view: true, edit: true },
                    'mod_movil_sc': { view: true, edit: true }, 'movil_sc_ver_ep': { view: true, edit: true }, 'movil_sc_generar_ep': { view: true, edit: true },
                    'mod_prevencion': { view: true, edit: true },
                    'mod_reportes': { view: false, edit: false },
                    'rep_reportes': { view: false, edit: false },
                    'rep_disenar': { view: false, edit: false }
                }
            }
        ];
        
        let added = false;
        let modified = false;
        const currentProfiles = globalProfiles.map(p => {
            const dp = defaultProfiles.find(d => d.id === p.id);
            const mergedPermissions = { ...p.permissions };
            let profileModified = false;
            
            if (dp) {
                Object.keys(dp.permissions).forEach(k => {
                    if (!mergedPermissions[k]) {
                        mergedPermissions[k] = { ...dp.permissions[k] };
                        profileModified = true;
                    }
                });
            }
            if (profileModified) {
                modified = true;
                return { ...p, permissions: mergedPermissions };
            }
            return p;
        });

        for (const dp of defaultProfiles) {
            if (!currentProfiles.some(p => p.id === dp.id)) {
                currentProfiles.push(dp);
                added = true;
            }
        }
        
        if (added || modified) {
            setGlobalProfiles(currentProfiles);
        }
        
        // Migrate legacy user roles automatically
        let usersModified = false;
        const currentUsers = [...globalUsers];
        for (let i = 0; i < currentUsers.length; i++) {
            // Convert legacy roles
            if (currentUsers[i].globalRole === 'SYSADMN' as any || currentUsers[i].globalRole === 'SYSADMIN' as any) {
                currentUsers[i] = { ...currentUsers[i], globalRole: 'Administrador' };
                usersModified = true;
            }
        }
        if (usersModified) {
            setGlobalUsers(currentUsers);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [globalProfiles.length, globalUsers.length]);

    const activeEmail = currentMsalAccount?.username.toLowerCase() || supabaseSession?.user?.email?.toLowerCase();
    const activeName = currentMsalAccount?.name || supabaseSession?.user?.email?.split('@')[0];

    React.useEffect(() => {
        if (activeEmail && globalUsers.length >= 0) {
            const email = activeEmail;
            const exists = globalUsers.find(u => u.email === email);
            if (!exists) {
                // Solo auto-asignamos si es un administrador inicial (admin@ o k-lydapp@) para el primer uso del sistema.
                // Cualquier otro usuario que no esté registrado explícitamente en la plataforma, será denegado.
                const isInitialAdmin = email.startsWith('admin@') || email.startsWith('k-lydapp@');
                if (isInitialAdmin) {
                    setGlobalUsers(prev => [...prev, {
                        id: currentMsalAccount?.localAccountId || supabaseSession?.user?.id || Math.random().toString(),
                        email: email,
                        name: activeName || email,
                        globalRole: 'Administrador',
                        status: 'Active',
                        assignedProjects: []
                    }]);
                } else {
                    setGlobalUsers(prev => [...prev, {
                        id: currentMsalAccount?.localAccountId || supabaseSession?.user?.id || Math.random().toString(),
                        email: email,
                        name: activeName || email,
                        globalRole: 'Usuario',
                        status: 'Pending',
                        assignedProjects: []
                    }]);
                }
            } else {
                // Solo actualizar el nombre si viene validado desde Microsoft Entra ID, 
                // o si es un usuario antiguo atrapado con el seudónimo 'Usuario Invitado'.
                const hasRealMsalName = !!currentMsalAccount?.name;
                const isLegacyGuest = exists.name === 'Usuario Invitado';

                if (activeName && exists.name !== activeName && (hasRealMsalName || isLegacyGuest)) {
                    setGlobalUsers(prev => prev.map(u => 
                        u.email === email 
                            ? { ...u, name: activeName || exists.name, status: 'Active' as const } 
                            : u
                    ));
                } else if (exists.status !== 'Active') {
                     // Solo activarlo si estaba pendiente
                     setGlobalUsers(prev => prev.map(u => 
                        u.email === email ? { ...u, status: 'Active' as const } : u
                    ));
                }
            }
        }
    }, [activeEmail, activeName, globalUsers, setGlobalUsers, currentMsalAccount, supabaseSession]);

    const currentUser = globalUsers.find(u => u.email === activeEmail);

    React.useEffect(() => {
        const nameToSave = currentUser?.name || activeName;
        if (nameToSave) {
            localStorage.setItem('klyd_current_user_name', nameToSave);
        }
    }, [currentUser, activeName]);

    const [selectedProjectId, setSelectedProjectId] = useState<string | null>(() => {
        const stored = localStorage.getItem('selectedProjectId');
        if (stored) return stored;
        // Si no hay guardado local, forzamos el default project del usuario actual si existe.
        return currentUser?.defaultProjectId || null;
    });

    const [hasAutoSelected, setHasAutoSelected] = useState(false);

    React.useEffect(() => {
        // En caso que no haya seleccionado nada pero carga por primera vez y tiene un default project:
        if (!selectedProjectId && currentUser?.defaultProjectId && !hasAutoSelected) {
            handleSelectProject(currentUser.defaultProjectId);
            setHasAutoSelected(true);
        } else if (!hasAutoSelected) {
            setHasAutoSelected(true);
        }
    }, [currentUser?.defaultProjectId, selectedProjectId, hasAutoSelected]);

    // Validar acceso al proyecto seleccionado y auto-seleccionar si tiene un único proyecto activo asignado
    React.useEffect(() => {
        if (currentUser) {
            const isSysAdmin = currentUser.globalRole === 'Administrador' || (currentUser.globalRole as any) === 'SYSADMIN';
            
            // Si el usuario no es admin y tiene un proyecto seleccionado al que no tiene acceso, lo deseleccionamos.
            if (!isSysAdmin && selectedProjectId) {
                const hasAccess = currentUser.assignedProjects.some(ap => ap.projectId === selectedProjectId);
                if (!hasAccess) {
                    setSelectedProjectId(null);
                    localStorage.removeItem('selectedProjectId');
                }
            }
            
            // Auto-selección inteligente si no hay proyecto seleccionado y tiene asignado exactamente un proyecto
            if (!isSysAdmin && !selectedProjectId && currentUser.assignedProjects && currentUser.assignedProjects.length === 1) {
                const singleProjId = currentUser.assignedProjects[0].projectId;
                const projExists = projects.find(p => p.id === singleProjId && p.status !== 'Inactive');
                if (projExists) {
                    handleSelectProject(singleProjId);
                }
            }
        }
    }, [currentUser, selectedProjectId, projects]);

    const handleSelectProject = (id: string) => {
        setSelectedProjectId(id);
        localStorage.setItem('selectedProjectId', id);
    };

    const handleCreateProject = (project: ProjectInfo & { id: string }) => {
        setProjects(prev => [...prev, project]);
        handleSelectProject(project.id);
    };

    const userPermissions = React.useMemo(() => {
        if (!currentUser) return null;
        if (currentUser.globalRole === 'Administrador' as any || currentUser.globalRole === 'SYSADMIN' as any) {
            return 'ADMIN_ALL';
        }
        if (!selectedProjectId) return null;
        
        const assignment = currentUser.assignedProjects.find(ap => ap.projectId === selectedProjectId);
        if (!assignment) return null;
        
        // Normalize the profile ID/Name from database to support legacy variants robustly
        let profileTarget = assignment.profileId || '';
        const normalizedTarget = profileTarget.trim().toLowerCase()
            .normalize("NFD").replace(/[\u0300-\u036f]/g, ""); // remove accents
            
        if (normalizedTarget === 'encargado de oficina tecnica' || normalizedTarget === 'prof-oficina') {
            profileTarget = 'Encargado de Oficina Técnica';
        } else if (normalizedTarget === 'asistente de oficina tecnica' || normalizedTarget === 'prof-asistente') {
            profileTarget = 'Asistente de Oficina Técnica';
        } else if (normalizedTarget === 'jefe de obra' || (normalizedTarget === 'jefe de obra (terreno)' || normalizedTarget === 'prof-jefe')) {
            profileTarget = 'Jefe de Obra (Terreno)';
        } else if (normalizedTarget === 'encargado de calidad' || normalizedTarget === 'prof-calidad') {
            profileTarget = 'Encargado de Calidad';
        } else if (normalizedTarget === 'prevencionista de riesgos' || normalizedTarget === 'prof-prevencion' || normalizedTarget === 'prevencionista') {
            profileTarget = 'Prevencionista de Riesgos';
        } else if (normalizedTarget === 'administrador de obra' || normalizedTarget === 'prof-admin' || normalizedTarget === 'administrador') {
            profileTarget = 'Administrador de Obra';
        } else if (normalizedTarget === 'equipo innovacion' || normalizedTarget === 'equipo innonvacion' || normalizedTarget === 'prof-innovacion' || normalizedTarget === 'innovacion') {
            profileTarget = 'Equipo Innovación';
        }

        const profile = globalProfiles.find(p => 
            p.id === profileTarget || 
            p.name === profileTarget || 
            p.id === assignment.profileId || 
            p.name === assignment.profileId
        );
        if (!profile) return null;
        return profile.permissions;
    }, [currentUser, selectedProjectId, globalProfiles]);

    const hasPermission = React.useCallback((module: string, action: 'view'|'edit') => {
        if (userPermissions === 'ADMIN_ALL') return true;
        if (!userPermissions) return false;
        if (module === 'mod_prevencion') return false; // Restringido temporalmente solo al Administrador de la plataforma
        const modPerms = (userPermissions as any)[module];
        return modPerms ? !!modPerms[action] : false;
    }, [userPermissions]);

    const currentProject = projects.find(p => p.id === selectedProjectId);

    const collectionName = selectedProjectId ? `project_${selectedProjectId}` : 'data';

    // Project Info
    // Project Info
    const [projectInfo] = useSyncState<ProjectInfo>(collectionName, 'projectInfo', {
        name: 'Cargando...',
        startDate: '2026-01-05',
        endDate: '2026-12-31'
    });

    // Local Tasks
    const [tasks, setTasks, forceSyncTasks] = useSyncState<Task[]>(collectionName, 'tasks', []);

    // EDT Data
    // EDT Data
    const [levels, setLevels] = useSyncState<Level[]>(collectionName, 'levels', []);
    const [units, setUnits] = useSyncState<PhysicalUnit[]>(collectionName, 'units', []);
    // Matrix Data: Key = `taskId_unitId`, Value = string
    // Matrix Data: Key = `taskId_unitId`, Value = string
    const [matrixData, setMatrixData] = useSyncState<Record<string, string>>(collectionName, 'matrixData', {});

    // Notifications state
    const [notifications, setNotifications] = useSyncState<AppNotification[]>(collectionName, 'notifications', []);

    // Responsibles Data (Dynamically computed from Global Directory)
    const responsibles: Responsible[] = React.useMemo(() => {
        if (!selectedProjectId) return [];
        return globalUsers
            .filter(u => u.globalRole === 'Administrador' || u.assignedProjects.some(ap => ap.projectId === selectedProjectId))
            .map(u => ({
                id: u.id,
                name: u.name,
                rut: '',
                email: u.email,
                role: u.globalRole === 'Administrador' ? 'Admin' : 'Usuario',
                profileId: u.assignedProjects.find(ap => ap.projectId === selectedProjectId)?.profileId || ''
            }));
    }, [globalUsers, selectedProjectId]);
    const setResponsibles = () => { console.warn('Responsibles are dynamically computed from globalUsers and cannot be overridden manually') };


    // Protocol Configuration Data
    // Key = taskId, Value = ProtocolConfig
    // Protocol Configuration Data
    // Key = taskId, Value = ProtocolConfig
    const [protocolConfigs, setProtocolConfigs] = useSyncState<Record<string, ProtocolConfig>>(collectionName, 'protocolConfigs', {});

    // Persistent Protocol State (Moved from QualityScreen)
    const [savedProtocols, setSavedProtocols] = useSyncState<Record<string, ProtocolState>>(collectionName, 'savedProtocols', {});

    // Persistent Weekly Plans (Moved from PlanningScreen)
    // Persistent Weekly Plans (Moved from PlanningScreen)
    const [weeklyPlans, setWeeklyPlans] = useSyncState<WeeklyPlan[]>(collectionName, 'weeklyPlans', []);

    // NEW: Persistent Lookahead Plans
    const [lookaheadPlans, setLookaheadPlans] = useSyncState<LookaheadPlan[]>(collectionName, 'lookaheadPlans', []);

    // Persistent Payment Data (Moved from PaymentsScreen)
    const [subcontractors, setSubcontractors] = useSyncState<Subcontractor[]>(collectionName, 'subcontractors', []);

    // Contracts (The Master Agreement)
    const [contracts, setContracts] = useSyncState<Contract[]>(collectionName, 'contracts', []);

    // Payment Statements (The actual Payments generated from Contracts)
    const [paymentStatements, setPaymentStatements] = useSyncState<PaymentStatement[]>(collectionName, 'paymentStatements', []);

    // --- Budget Data State ---
    // --- Budget Data State ---
    const [budgetProjects, setBudgetProjects] = useSyncState<ResourceProject[]>(collectionName, 'budgetProjects', []);
    const [budgetResources, setBudgetResources] = useSyncState<ResourceItem[]>(collectionName, 'budgetResources', []);
    const [budgetItems, setBudgetItems] = useSyncState<BudgetItem[]>(collectionName, 'budgetItems', []);
    const [apuData, setApuData] = useSyncState<Record<string, ApuItem[]>>(collectionName, 'apuData', {});
    const [budgetTree, setBudgetTree] = useSyncState<BudgetFolder[]>(collectionName, 'budgetTree', []);

    // Subcontract Types
    const [subcontractTypes, setSubcontractTypes] = useSyncState<SubcontractType[]>(collectionName, 'subcontractTypes', []);

    // CNC Groups
    const [cncGroups, setCncGroups] = useSyncState<CNCGroup[]>(collectionName, 'cncGroups', [
        {
            id: 'g1',
            name: 'Materiales',
            causes: []
        }
    ]);

    // Constraint Groups
    const [constraintGroups, setConstraintGroups] = useSyncState<ConstraintGroup[]>(collectionName, 'constraintGroups', [
        {
            id: 'cg1',
            name: 'Ingeniería',
            definitions: [
                { id: 'cd1', name: 'Falta planos detalle' },
                { id: 'cd2', name: 'RDI Pendiente' }
            ]
        },
        {
            id: 'cg2',
            name: 'Abastecimiento',
            definitions: [
                { id: 'cd3', name: 'Falta Materiales' },
                { id: 'cd4', name: 'Falta Equipos' }
            ]
        }
    ]);

    // Gantt Baselines History
    const [baselines, setBaselines] = useSyncState<GanttBaseline[]>(collectionName, 'baselines', []);
    const [progressHistory, setProgressHistory] = useSyncState<ProjectProgressSnapshot[]>(collectionName, 'progressHistory', []);

    // Recent Activities
    const [activities, setActivities] = useSyncState<Activity[]>(collectionName, 'activities', []);


    const addActivity = (activity: Omit<Activity, 'id' | 'timestamp' | 'count'>) => {
        // Resolve user dynamically from session/currentUser if it's "Usuario", empty or missing
        let resolvedUser = activity.user;
        const cachedName = typeof window !== 'undefined' ? localStorage.getItem('klyd_current_user_name') : null;
        if (!resolvedUser || resolvedUser === 'Usuario') {
            resolvedUser = currentUser?.name || currentUser?.email || cachedName || 'Usuario';
        }

        const activityWithUser = {
            ...activity,
            user: resolvedUser
        };

        setActivities(prev => {
            const currentActivities = prev || [];
            if (currentActivities.length > 0) {
                const last = currentActivities[0];
                // Group if it has the same title, type and user (sequential)
                if (last.title === activityWithUser.title && last.type === activityWithUser.type && last.user === activityWithUser.user) {
                    const updatedLast: Activity = {
                        ...last,
                        count: (last.count || 1) + 1,
                        timestamp: new Date().toISOString() // Update timestamp to show latest
                    };
                    return [updatedLast, ...currentActivities.slice(1)];
                }
            }

            const newActivity: Activity = {
                ...activityWithUser,
                id: Date.now().toString(),
                timestamp: new Date().toISOString(),
                count: 1
            };
            return [newActivity, ...currentActivities].slice(0, 15);
        });
    };

    const [bypassedWaitingRoom, setBypassedWaitingRoom] = useState(false);

    const handleLogout = async () => {
        if (currentMsalAccount) {
            instance.logoutRedirect();
        }
        await supabase.auth.signOut();
    };

    const isAuthenticated = !!activeEmail;

    if (checkingSession) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-[#0d131b] font-display">
                <div className="animate-pulse text-slate-400 font-bold uppercase tracking-widest text-sm">Restaurando Sesión...</div>
            </div>
        );
    }

    return (
        <>
            {isAuthenticated ? (
                <BrowserRouter>
                    {!currentUser ? (
                        <div className="min-h-screen flex flex-col gap-4 items-center justify-center bg-slate-50 dark:bg-[#0d131b] font-display text-center p-6">
                            <div className="relative w-16 h-16 flex items-center justify-center mb-2">
                                <span className="material-symbols-outlined text-[40px] text-primary animate-pulse relative z-10">fingerprint</span>
                                <div className="absolute inset-0 border-4 border-primary/20 rounded-full animate-spin border-t-primary"></div>
                            </div>
                            <div className="text-slate-700 dark:text-slate-200 font-bold text-2xl">Validando acceso...</div>
                            <div className="text-slate-500 dark:text-slate-400 text-sm max-w-sm mt-2 mb-4">
                                Estamos verificando tu identidad y recuperando tu perfil.
                            </div>
                        </div>
                    ) : currentUser.status === 'Pending' ? (
                        <div className="min-h-screen flex flex-col gap-4 items-center justify-center bg-slate-50 dark:bg-[#0d131b] font-display text-center p-6">
                            <div className="rounded-2xl bg-orange-100 dark:bg-orange-900/30 p-6 mb-2 mt-4 inline-flex">
                                <span className="material-symbols-outlined text-[64px] text-orange-600 dark:text-orange-400">group_add</span>
                            </div>
                            <div className="text-slate-800 dark:text-slate-100 font-black text-3xl tracking-tight">Sala de Espera</div>
                            <div className="text-slate-500 dark:text-slate-400 text-sm max-w-md mt-2 mb-6 text-center">
                                Tu cuenta de correo ({activeEmail}) ha sido verificada correctamente, pero un Administrador General debe autorizar tu ingreso y asignarte los proyectos correspondientes para operar.
                            </div>
                            <button onClick={handleLogout} className="text-sm font-bold text-slate-500 hover:text-slate-800 dark:hover:text-slate-300 underline underline-offset-4">Cerrar Sesión</button>
                        </div>
                    ) : showAdminPanel && currentUser.globalRole === 'Administrador' ? (
                        <GlobalAdminPanel 
                            onBack={() => setShowAdminPanel(false)}
                            globalUsers={globalUsers}
                            setGlobalUsers={setGlobalUsers}
                            globalProfiles={globalProfiles}
                            setGlobalProfiles={setGlobalProfiles}
                            projects={projects}
                            setProjects={setProjects}
                            companyConfig={companyConfig}
                            globalCNCGroups={globalCNCGroups} setGlobalCNCGroups={setGlobalCNCGroups}
                            globalConstraintGroups={globalConstraintGroups} setGlobalConstraintGroups={setGlobalConstraintGroups}
                            globalSubcontractTypes={globalSubcontractTypes} setGlobalSubcontractTypes={setGlobalSubcontractTypes}
                            globalProtocolTemplates={globalProtocolTemplates} setGlobalProtocolTemplates={setGlobalProtocolTemplates}
                            globalSSOMAChecklists={globalSSOMAChecklists} setGlobalSSOMAChecklists={setGlobalSSOMAChecklists}
                            globalSSOMAEvents={globalSSOMAEvents} setGlobalSSOMAEvents={setGlobalSSOMAEvents}
                            incidences={incidences}
                            setIncidences={setIncidences}
                            notifications={notifications}
                            setNotifications={setNotifications}
                        />
                    ) : currentUser.globalRole !== 'Administrador' && currentUser.assignedProjects.length === 0 && !bypassedWaitingRoom ? (
                        <WaitingRoom onBypass={() => setBypassedWaitingRoom(true)} />
                    ) : !selectedProjectId ? (
                        <ProjectSelection
                            projects={projects}
                            onSelectProject={handleSelectProject}
                            onCreateProject={handleCreateProject}
                            companyConfig={companyConfig}
                            onUpdateCompanyConfig={setCompanyConfig}
                            currentUser={currentUser}
                            onOpenAdmin={() => setShowAdminPanel(true)}
                        />
                    ) : (
                        <div className="flex h-screen w-full overflow-hidden bg-background-light dark:bg-background-dark text-[#0d131b] dark:text-white font-display">
                            <Sidebar 
                                onBack={() => {
                                    setSelectedProjectId(null);
                                    localStorage.removeItem('selectedProjectId');
                                }} 
                                currentUser={currentUser}
                                onLogout={handleLogout}
                                hasPermission={hasPermission}
                                companyConfig={companyConfig}
                                isNative={isNative}
                            />
                            <div className="flex-1 overflow-hidden relative flex flex-col">
                                <MobileHeader 
                                    onBack={() => {
                                        setSelectedProjectId(null);
                                        localStorage.removeItem('selectedProjectId');
                                    }} 
                                    currentUser={currentUser}
                                    onToggleMenu={() => setIsMobileMenuOpen(true)}
                                    notifications={notifications}
                                    setNotifications={setNotifications}
                                    companyConfig={companyConfig}
                                    isNative={isNative}
                                    onLogout={handleLogout}
                                />
                                <div className="flex-1 overflow-hidden relative">
                                    <ErrorBoundary>
                                    <Routes>
                                    <Route path="/" element={
                                        <DashboardScreen
                                            projectInfo={currentProject || projectInfo}
                                            tasks={tasks}
                                            responsibles={responsibles}
                                            savedProtocols={savedProtocols}
                                            contracts={contracts}
                                            paymentStatements={paymentStatements}
                                            activities={activities}
                                            progressHistory={progressHistory}
                                            units={units}
                                            matrixData={matrixData}
                                            weeklyPlans={weeklyPlans}
                                            protocolConfigs={protocolConfigs}
                                            companyConfig={companyConfig}
                                            lookaheadPlans={lookaheadPlans}
                                            notifications={notifications}
                                            setNotifications={setNotifications}
                                            currentUser={currentUser}
                                            hasPermission={hasPermission}
                                            subcontractors={subcontractors}
                                            isNative={isNative}
                                            baselines={baselines}
                                        />
                                    } />
                                    <Route path="/edt" element={
                                        <EDTScreen
                                            projectInfo={currentProject || projectInfo}
                                            tasks={tasks} setTasks={setTasks}
                                            levels={levels} setLevels={setLevels}
                                            units={units} setUnits={setUnits}
                                            matrixData={matrixData} setMatrixData={setMatrixData}
                                            budgetItems={budgetItems}
                                            apuData={apuData} setApuData={setApuData}
                                            budgetResources={budgetResources}
                                            addActivity={addActivity}
                                            hasPermission={hasPermission}
                                        />
                                    } />
                                    <Route path="/budget" element={
                                        <BudgetScreen
                                            tasks={tasks}
                                            budgetTree={budgetTree} setBudgetTree={setBudgetTree}
                                            budgetItems={budgetItems} setBudgetItems={setBudgetItems}
                                            apuData={apuData} setApuData={setApuData}
                                            budgetResources={budgetResources} setBudgetResources={setBudgetResources}
                                            budgetProjects={budgetProjects} setBudgetProjects={setBudgetProjects}
                                            projectInfo={currentProject || projectInfo}
                                        />
                                    } />
                                    <Route path="/planning" element={
                                        <PlanningScreen
                                            tasks={tasks} setTasks={setTasks}
                                            responsibles={responsibles} setResponsibles={setResponsibles}
                                            globalUsers={globalUsers} setGlobalUsers={setGlobalUsers}
                                            matrixData={matrixData}
                                            units={units}
                                            levels={levels}
                                            savedProtocols={savedProtocols}
                                            protocolConfigs={protocolConfigs}
                                            subcontractTypes={subcontractTypes}
                                            setSubcontractTypes={setSubcontractTypes}
                                            weeklyPlans={weeklyPlans}
                                            setWeeklyPlans={setWeeklyPlans}
                                            lookaheadPlans={lookaheadPlans}
                                            setLookaheadPlans={setLookaheadPlans}
                                            projectInfo={currentProject || projectInfo}
                                            cncGroups={cncGroups} setCncGroups={setCncGroups}
                                            constraintGroups={constraintGroups} setConstraintGroups={setConstraintGroups}
                                            globalCNCGroups={globalCNCGroups}
                                            globalConstraintGroups={globalConstraintGroups}
                                            forceSyncTasks={forceSyncTasks}
                                            profiles={globalProfiles}
                                            addActivity={addActivity}
                                            notifications={notifications}
                                            setNotifications={setNotifications}
                                            baselines={baselines}
                                            setBaselines={setBaselines}
                                            progressHistory={progressHistory}
                                            setProgressHistory={setProgressHistory}
                                            currentUser={currentUser}
                                            hasPermission={hasPermission}
                                        />
                                    } />
                                    <Route path="/quality" element={
                                        <QualityScreen
                                            tasks={tasks}
                                            responsibles={responsibles}
                                            protocolConfigs={protocolConfigs}
                                            setProtocolConfigs={setProtocolConfigs}
                                            units={units}
                                            levels={levels}
                                            matrixData={matrixData}
                                            weeklyPlans={weeklyPlans}
                                            savedProtocols={savedProtocols}
                                            setSavedProtocols={setSavedProtocols}
                                            projectInfo={currentProject || projectInfo}
                                            addActivity={addActivity}
                                            companyConfig={companyConfig}
                                            currentUser={currentUser}
                                            hasPermission={hasPermission}
                                            globalProtocolTemplates={globalProtocolTemplates}
                                        />
                                    } />
                                    <Route path="/payments" element={
                                        <PaymentsScreen
                                            tasks={tasks}
                                            units={units}
                                            levels={levels}
                                            matrixData={matrixData}
                                            protocolConfigs={protocolConfigs}
                                            subcontractors={subcontractors}
                                            setSubcontractors={setSubcontractors}
                                            contracts={contracts}
                                            setContracts={setContracts}
                                            paymentStatements={paymentStatements}
                                            setPaymentStatements={setPaymentStatements}
                                            savedProtocols={savedProtocols}
                                            subcontractTypes={subcontractTypes}
                                            setSubcontractTypes={setSubcontractTypes}
                                            projectInfo={currentProject || projectInfo}
                                            addActivity={addActivity}
                                            companyConfig={companyConfig}
                                            hasPermission={hasPermission}
                                            isPlatformAdmin={userPermissions === 'ADMIN_ALL'}
                                            globalUsers={globalUsers}
                                            responsibles={responsibles}
                                        />
                                    } />
                                    <Route path="/prevention" element={
                                        hasPermission('mod_prevencion', 'view') ? (
                                            <PreventionScreen
                                                projectInfo={currentProject || projectInfo}
                                                currentUser={currentUser}
                                                hasPermission={hasPermission}
                                                responsibles={responsibles}
                                                addActivity={addActivity}
                                                levels={levels}
                                                units={units}
                                                setNotifications={setNotifications}
                                                globalSSOMAEvents={globalSSOMAEvents}
                                            />
                                        ) : (
                                            <Navigate to="/" replace />
                                        )
                                    } />
                                    <Route path="/reports" element={
                                        hasPermission('mod_reportes', 'view') ? (
                                            <ReportsScreen
                                                budgetItems={budgetItems}
                                                tasks={tasks}
                                                weeklyPlans={weeklyPlans}
                                                lookaheadPlans={lookaheadPlans}
                                                savedProtocols={savedProtocols}
                                                contracts={contracts}
                                                paymentStatements={paymentStatements}
                                                responsibles={responsibles}
                                                cncGroups={cncGroups}
                                                constraintGroups={constraintGroups}
                                                subcontractors={subcontractors}
                                                units={units}
                                                matrixData={matrixData}
                                                protocolConfigs={protocolConfigs}
                                                projectInfo={currentProject || projectInfo}
                                                progressHistory={progressHistory}
                                                currentUser={currentUser}
                                                hasPermission={hasPermission}
                                                baselines={baselines}
                                            />
                                        ) : (
                                            <Navigate to="/" replace />
                                        )
                                    } />
                                    <Route path="/auth-callback" element={<AuthCallbackRedirect />} />
                                    <Route path="/auth" element={<AuthCallbackRedirect />} />
                                    </Routes>
                                    </ErrorBoundary>
                                    <AIChatWidget currentUser={currentUser} />
                                    {hasPermission('mod_incidencias', 'view') && (
                                        <ReportBugWidget currentUser={currentUser} currentProject={currentProject} onAddIncidence={(inc) => setIncidences(prev => [inc, ...prev])} />
                                    )}
                                </div>
                                <MobileTabBar hasPermission={hasPermission} onToggleMenu={() => setIsMobileMenuOpen(true)} isNative={isNative} currentUser={currentUser} />
                                <MobileMenuDrawer 
                                    isOpen={isMobileMenuOpen} 
                                    onClose={() => setIsMobileMenuOpen(false)} 
                                    onLogout={handleLogout} 
                                    hasPermission={hasPermission}
                                    isNative={isNative}
                                />
                            </div>
                        </div>
                    )}
                </BrowserRouter>
            ) : (
                <LoginScreen />
            )}

            {showUpdateModal && updateInfo && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-[#1a2634] border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden p-6 flex flex-col gap-6 text-slate-800 dark:text-slate-100 animate-in fade-in zoom-in duration-200">
                        {/* Header with Icon */}
                        <div className="flex items-start gap-4">
                            <div className="p-3 bg-primary/10 text-primary rounded-xl flex items-center justify-center">
                                <span className="material-symbols-outlined text-[32px]">system_update</span>
                            </div>
                            <div className="flex-1">
                                <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                                    Nueva versión disponible
                                </h3>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                                    K-LyDApp v{updateInfo.latest.latest_version} (Build {updateInfo.latest.latest_build})
                                </p>
                            </div>
                        </div>

                        {/* Body Content */}
                        <div className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                            <p>
                                Hay una nueva versión de K-LyDApp disponible. Te recomendamos actualizar para obtener mejoras y correcciones recientes.
                            </p>
                            {updateInfo.latest.message && (
                                <div className="mt-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg text-xs text-slate-500 dark:text-slate-400 max-h-24 overflow-y-auto italic">
                                    {updateInfo.latest.message}
                                </div>
                            )}
                            <div className="mt-4 flex flex-col gap-1 border-t border-slate-100 dark:border-slate-800 pt-3 text-xs text-slate-400">
                                <div className="flex justify-between">
                                    <span>Versión instalada:</span>
                                    <span className="font-semibold">{updateInfo.installed.version} (Build {updateInfo.installed.build})</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Versión disponible:</span>
                                    <span className="font-semibold text-primary">{updateInfo.latest.latest_version} (Build {updateInfo.latest.latest_build})</span>
                                </div>
                            </div>
                        </div>

                        {/* Footer Buttons */}
                        <div className="flex items-center gap-3 justify-end border-t border-slate-100 dark:border-slate-800 pt-4">
                            <button
                                onClick={handleDismissUpdate}
                                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 dark:text-slate-300 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                            >
                                Más tarde
                            </button>
                            <button
                                onClick={handlePerformUpdate}
                                className="px-5 py-2.5 text-sm font-bold text-white bg-primary hover:bg-primary/90 rounded-lg shadow transition-colors flex items-center gap-2"
                            >
                                <span className="material-symbols-outlined text-[18px]">download</span>
                                Actualizar ahora
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default App;
